<!--
  Partner — creazione guidata: stepper sticky a sinistra, quattro schede,
  barra azioni fissa in basso. Il tipo di partner (`is_restaurant`) si
  sceglie al primo passaggio.

  Cose non ovvie:

  1) LA CREAZIONE NON È ATOMICA. Non esiste un endpoint unico: si creano in
     sequenza partner → sedi → fasce → dipendenti, con chiamate separate. Se
     un passaggio fallisce, quelli prima restano creati. Per questo il
     risultato viene riportato per esteso invece di un generico "errore": chi
     usa la pagina deve sapere cosa esiste già e cosa va completato a mano.

  2) LE FASCE CONTANO SOLO CON `TARIFF_ENGINE=bands`. Senza quel flag il
     prezzo esce dalle 64 colonne legacy, che qui NON vengono scritte (hanno
     passi fissi 5/10/…/30 km e non possono rappresentare fasce libere): il
     partner risulta senza listino e i booking quotano 0. L'avviso è a
     schermo, non solo in questo commento.

     ⚠️ IL SERVIZIO TARIFFARIO NON È QUELLO SCELTO A UI. Il motore cerca la
     fascia per servizio ESATTO, e l'auto di cortesia è un servizio a sé
     (`pickup_loaner`), come lo è il notturno fra le 22 e le 6
     (`restaurant_night`). Per questo esistono tabelle dedicate: una per il
     pickup con auto di cortesia, e DUE per il notturno — diurna e serale.
     Il backend sceglie in base all'ora di partenza (`hour >= 22 || hour < 6`
     in pickup.service.ts), quindi sono due listini che possono avere prezzi
     diversi: si compilano separatamente, con un pulsante per allinearli
     quando coincidono.

     La QUOTA PARTNER (`is_partner_share = true`) è un listino parallelo con
     le stesse fasce km: sta nella colonna in fondo a ogni tabella e diventa
     una riga separata al salvataggio.

  3) L'ADVISOR SI SCRIVE SULL'ADVISOR. La M2M è dichiarata sul solo Advisor,
     quindi l'associazione si fa con un PATCH su /advisor, non sul partner.

  4) INDIRIZZO DELLA SEDE: via e CIVICO sono due colonne separate (`address`
     e `street_number`). Scrivere il civico dentro la via lascia
     `street_number` vuoto e manda in cerca un indirizzo diverso: la sede
     finisce con coordinate sbagliate e il planner non trova gli orari. Per lo
     stesso motivo la geocodifica parte SOLO a indirizzo completo, non a ogni
     campo lasciato.

  5) Regione e Provincia si scelgono da elenco (`region` + `province_ref`);
     la colonna stringa `province` resta e viene tenuta allineata alla SIGLA
     della provincia scelta: è la provincia della sede che il planner usa per
     cercare i driver.
-->
<template>
  <div class="apc">
    <div class="apc__head">
      <button
        type="button"
        class="apc__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="apc__title">
          Nuovo partner
        </h1>
        <p class="apc__sub">
          Creazione guidata — tipo, dati, sedi, tariffe e dipendenti
        </p>
      </div>
    </div>

    <div class="apc__layout">
      <!-- ============================== STEPPER ============================ -->
      <nav class="stepper">
        <button
          v-for="(s, i) in STEPS"
          :key="s.id"
          type="button"
          class="step"
          :class="{ 'is-active': activeStep === s.id }"
          @click="goToStep(s.id)"
        >
          <span class="step__n">{{ i + 1 }}</span>
          <span class="step__t">
            {{ s.title }}
            <small>{{ s.hint }}</small>
          </span>
        </button>
      </nav>

      <div class="apc__col">
        <!-- ========================= 1 · DATI PARTNER ===================== -->
        <section
          id="s1"
          ref="s1"
          class="card"
        >
          <div class="card__hd">
            <span class="card__n">1</span>
            <h3>Dati partner</h3>
          </div>
          <div class="card__bd">
            <div class="blocklabel">
              Tipo di partner
            </div>
            <div class="typecards">
              <label
                v-for="t in TYPES"
                :key="t.value"
                class="tcard"
                :class="{ 'is-on': form.type === t.value }"
              >
                <input
                  v-model="form.type"
                  type="radio"
                  :value="t.value"
                >
                <span class="tcard__in">
                  <span class="tcard__ico">{{ t.icon }}</span>
                  <span>
                    <span class="tcard__t">{{ t.label }}</span>
                    <span class="tcard__h">{{ t.hint }}</span>
                  </span>
                </span>
              </label>
            </div>

            <!-- i servizi si scelgono solo per l'automotive: il notturno ha
                 il proprio listino, non questa combinazione -->
            <template v-if="isAutomotive">
              <div class="blocklabel blocklabel--spaced">
                Servizi attivi
              </div>
              <div class="svcsel">
                <label
                  v-for="s in SERVICES"
                  :key="s.key"
                  class="svcbox"
                  :class="{ 'is-on': form.services[s.key] }"
                >
                  <input
                    v-model="form.services[s.key]"
                    type="checkbox"
                  >
                  <span class="svcbox__b">{{ s.label }}</span>
                </label>
              </div>
            </template>

            <div class="blocklabel blocklabel--spaced">
              Anagrafica
            </div>
            <div class="grid2">
              <div class="fld">
                <label>Ragione sociale</label>
                <el-input
                  v-model="form.company_name"
                  placeholder="Es. Autotorino S.p.A."
                />
              </div>
              <div class="fld">
                <label>Partita IVA</label>
                <el-input v-model="form.piva" />
              </div>
              <div class="fld">
                <label>Email</label>
                <el-input
                  v-model="form.mail"
                  placeholder="amministrazione@partner.it"
                />
              </div>
              <div class="fld">
                <label>Telefono</label>
                <el-input v-model="form.phone_number" />
              </div>
              <div class="fld">
                <label>Provvigione</label>
                <el-input
                  v-model.number="form.provvigione"
                  type="number"
                >
                  <template #append>
                    %
                  </template>
                </el-input>
              </div>
              <div class="fld">
                <label>Advisor <span class="opt">(chi segue il partner)</span></label>
                <el-select
                  v-model="form.advisorId"
                  placeholder="— Nessuno —"
                  clearable
                  filterable
                  style="width: 100%"
                >
                  <el-option
                    v-for="a in advisors"
                    :key="a.id"
                    :label="[a.name, a.surname].filter(Boolean).join(' ')"
                    :value="a.id"
                  />
                </el-select>
              </div>
            </div>

            <div class="blocklabel blocklabel--spaced">
              Pagamenti — Nexi
            </div>
            <div class="grid3">
              <div class="fld">
                <label>API key</label>
                <el-input
                  v-model="form.API_key"
                  placeholder="Chiave fornita da Nexi"
                />
              </div>
              <div class="fld">
                <label>Dominio</label>
                <el-input v-model="form.domain" />
              </div>
              <div class="fld">
                <label>Alias</label>
                <el-input
                  v-model="form.alias"
                  placeholder="es. ALIAS_WEB_00012345"
                />
              </div>
            </div>

            <div class="toggle-row">
              <div class="toggle-row__txt">
                <div class="toggle-row__t">
                  Costi aggiuntivi
                </div>
                <div class="toggle-row__h">
                  Se attivo, sui booking di questo partner andranno compilati i
                  costi aggiuntivi (inseriti a mano da Simba sugli scontrini dei
                  driver).
                </div>
              </div>
              <el-switch v-model="form.has_extra_costs" />
            </div>
          </div>
        </section>

        <!-- ============================= 2 · SEDI ========================= -->
        <section
          id="s2"
          ref="s2"
          class="card"
        >
          <div class="card__hd">
            <span class="card__n">2</span>
            <h3>Sedi</h3>
            <span class="apc__sp" />
            <span class="hint">{{ form.headquarters.length }} sedi</span>
          </div>
          <div class="card__bd">
            <div
              v-for="(h, i) in form.headquarters"
              :key="i"
              class="subcard"
            >
              <div class="subcard__sh">
                <span class="subcard__lbl">Sede {{ i + 1 }}</span>
                <span class="apc__sp" />
                <el-button
                  v-if="form.headquarters.length > 1"
                  type="danger"
                  plain
                  size="small"
                  @click="removeHeadquarter(i)"
                >
                  Rimuovi
                </el-button>
              </div>
              <div class="grid2">
                <div class="fld">
                  <label>Nome sede</label>
                  <el-input
                    v-model="h.name"
                    placeholder="Es. Milano Centro"
                  />
                </div>
                <!-- ⚠️ Via e CIVICO sono due colonne diverse a database
                     (`address` e `street_number`). Scrivere il civico dentro
                     la via lascia `street_number` vuoto e falsa la
                     geocodifica: da lì in poi la sede ha coordinate sbagliate
                     e il planner non trova gli orari. -->
                <div class="fld">
                  <label>Indirizzo</label>
                  <el-input
                    v-model="h.address"
                    placeholder="Via/piazza, senza civico"
                    @blur="maybeGeocode(h)"
                  />
                </div>
                <div class="fld">
                  <label>Civico</label>
                  <el-input
                    v-model="h.street_number"
                    placeholder="Es. 12/A"
                    @blur="maybeGeocode(h)"
                  />
                </div>
                <div class="fld">
                  <label>Città</label>
                  <el-input
                    v-model="h.city"
                    @blur="maybeGeocode(h)"
                  />
                </div>
                <div class="fld">
                  <label>CAP</label>
                  <el-input
                    v-model="h.cap"
                    @blur="maybeGeocode(h)"
                  />
                </div>
                <div class="fld">
                  <label>Regione</label>
                  <el-select
                    v-model="h.regionId"
                    placeholder="Scegli la regione"
                    filterable
                    style="width: 100%"
                    @change="onRegionChange(h)"
                  >
                    <el-option
                      v-for="r in regions"
                      :key="r.id"
                      :label="r.name"
                      :value="r.id"
                    />
                  </el-select>
                </div>
                <div class="fld">
                  <label>Provincia</label>
                  <el-select
                    v-model="h.provinceId"
                    placeholder="Scegli la provincia"
                    filterable
                    :disabled="!h.regionId"
                    style="width: 100%"
                    @change="onProvinceChange(h)"
                  >
                    <el-option
                      v-for="p in provincesOf(h)"
                      :key="p.id"
                      :label="`${p.name} (${p.sigla})`"
                      :value="p.id"
                    />
                  </el-select>
                </div>
                <!-- La geocodifica parte solo a indirizzo COMPLETO: farla a
                     ogni campo significa cercare un indirizzo a metà. -->
                <div class="fld fld--full">
                  <label>Coordinate</label>
                  <div class="geoRow">
                    <el-button
                      :loading="h.geoState === 'loading'"
                      @click="geocode(h, true)"
                    >
                      Verifica indirizzo
                    </el-button>
                    <div
                      v-if="h.geoState === 'ok'"
                      class="geo"
                    >
                      📍 Confermato: <b>{{ h.geoLabel }}</b>
                    </div>
                    <div
                      v-else-if="h.geoState === 'fail'"
                      class="geo geo--fail"
                    >
                      ⚠ Indirizzo non trovato: la sede resterebbe senza
                      coordinate
                    </div>
                    <div
                      v-else-if="h.geoState === 'incomplete'"
                      class="geo geo--wait"
                    >
                      Compila via, civico, CAP, città e provincia: si cerca
                      solo l'indirizzo completo
                    </div>
                    <div
                      v-else-if="h.geoState === 'loading'"
                      class="geo geo--wait"
                    >
                      Ricerca indirizzo…
                    </div>
                  </div>
                </div>
                <div class="fld">
                  <label>Abbonamento mensile</label>
                  <el-input
                    v-model.number="h.abbonamento"
                    type="number"
                    step="0.01"
                  >
                    <template #append>
                      €
                    </template>
                  </el-input>
                </div>
              </div>
            </div>

            <el-button
              class="apc__add"
              @click="addHeadquarter"
            >
              ＋ Aggiungi sede
            </el-button>
            <p class="hint hint--block">
              L'indirizzo viene geolocalizzato e confermato sotto il campo: senza
              coordinate la sede non entra nei calcoli di distanza.
            </p>
          </div>
        </section>

        <!-- ====================== 3 · TARIFFE & VALET ===================== -->
        <section
          id="s3"
          ref="s3"
          class="card"
        >
          <div class="card__hd">
            <span class="card__n">3</span>
            <h3>Tariffe &amp; Valet</h3>
            <span class="apc__sp" />
            <span class="hint">tipo: {{ isAutomotive ? 'Automotive' : 'Notturno' }}</span>
          </div>
          <div class="card__bd">
            <!-- NOTTURNO: DUE listini, diurno e serale -->
            <template v-if="!isAutomotive">
              <div class="subcard">
                <div class="subcard__sh">
                  <span class="subcard__lbl">Tariffe Notturno · diurne</span>
                  <span class="apc__sp" />
                  <span class="hint">corse dalle 06:00 alle 22:00</span>
                </div>
                <BandTable
                  v-model="form.bands.restaurant"
                  :default-mode="defaultModeFor('restaurant')"
                  :with-share="true"
                />
              </div>

              <div class="subcard">
                <div class="subcard__sh">
                  <span class="subcard__lbl">Tariffe Notturno · serali</span>
                  <span class="apc__sp" />
                  <span class="hint">corse dalle 22:00 alle 06:00</span>
                  <el-button
                    size="small"
                    @click="copyDayToNight"
                  >
                    Copia dalle diurne
                  </el-button>
                </div>
                <BandTable
                  v-model="form.bands.restaurant_night"
                  :default-mode="defaultModeFor('restaurant_night')"
                  :with-share="true"
                />
                <div class="notebox">
                  Il backend applica <b>questo</b> listino alle corse che
                  partono fra le <b>22:00 e le 06:00</b>, e quello diurno a
                  tutte le altre. Per la maggior parte dei locali i due
                  coincidono — in quel caso basta «Copia dalle diurne» — ma se
                  restano vuote le corse serali quoterebbero 0.
                </div>
              </div>
            </template>

            <template v-else>
              <div
                v-if="form.services.pickup"
                class="subcard"
              >
                <div class="subcard__sh">
                  <span class="subcard__lbl">Tariffe Pickup</span>
                </div>
                <BandTable
                  v-model="form.bands.pickup"
                  :default-mode="defaultModeFor('pickup')"
                  :with-share="true"
                />
              </div>

              <!-- L'auto di cortesia è un SERVIZIO a sé per il motore
                   (`pickup_loaner`): senza queste fasce un pickup con auto di
                   cortesia quota 0, anche se il pickup normale ha il listino. -->
              <div
                v-if="form.services.pickup"
                class="subcard"
              >
                <div class="subcard__sh">
                  <span class="subcard__lbl">Tariffe Pickup con auto di cortesia</span>
                  <span class="apc__sp" />
                  <el-button
                    v-if="!form.bands.pickup_loaner.length && form.bands.pickup.length"
                    size="small"
                    text
                    @click="copyPickupBands"
                  >
                    Copia dal listino pickup
                  </el-button>
                </div>
                <BandTable
                  v-model="form.bands.pickup_loaner"
                  :default-mode="defaultModeFor('pickup_loaner')"
                  :with-share="true"
                />
              </div>

              <!-- il valet è legato al pickup: senza quel servizio non ha senso -->
              <div
                v-if="form.services.pickup"
                class="subcard"
              >
                <div class="subcard__sh">
                  <span class="subcard__lbl">Pacchetto valet</span>
                  <span class="apc__sp" />
                  <span class="hint">venduto su finanziamento → COD2</span>
                </div>
                <!-- Listino a pacchetti: il pacchetto si prezza per fascia km. -->
                <div class="hint hint--block">
                  Listino per fascia km: prezzo Simba di <b>un pickup all'anno</b>, per
                  pacchetti da 1, 2 o 3 pickup all'anno. Il pacchetto costa mesi ×
                  pickup all'anno × prezzo mensile; i prezzi di vendita li imposta il
                  partner dal suo gestionale. I km che sforano il pacchetto si
                  pagano al <b>prezzo al km</b> indicato per la fascia.
                </div>
                <ValetBandTable v-model="form.valetBands" />
                <div class="notebox">
                  I km <b>oltre la fascia compresa nel pacchetto</b> si addebitano con le
                  <b>tariffe fleet dello spostamento singolo</b>.
                </div>
              </div>

              <div
                v-if="form.services.fleet"
                class="subcard"
              >
                <div class="subcard__sh">
                  <span class="subcard__lbl">Tariffe Fleet (spostamento interno)</span>
                </div>
                <!-- La quota partner si vede anche qui: nasce uguale alla
                     tariffa Simba e viene salvata, quindi deve essere sotto
                     gli occhi di chi crea il partner, non nascosta. -->
                <BandTable
                  v-model="form.bands.journey"
                  :default-mode="defaultModeFor('journey')"
                  :two-operators="true"
                  :with-share="true"
                />
              </div>

              <!-- Listino dei veicoli NON immatricolati, che si spostano con la
                   targa prova: stessa struttura del Fleet, importi suoi. È
                   facoltativo, quindi nasce vuoto: senza, quelle corse si
                   tariffano con il listino Fleet qui sopra. -->
              <div
                v-if="form.services.fleet"
                class="subcard"
              >
                <div class="subcard__sh">
                  <span class="subcard__lbl">Tariffe Fleet · targa prova</span>
                  <span class="apc__sp" />
                  <span class="hint">veicoli non immatricolati</span>
                  <el-button
                    v-if="!form.bands.journey_prova.length && form.bands.journey.length"
                    size="small"
                    text
                    @click="copyJourneyToProva"
                  >
                    Copia dal listino Fleet
                  </el-button>
                </div>
                <BandTable
                  v-model="form.bands.journey_prova"
                  :default-mode="defaultModeFor('journey')"
                  :two-operators="true"
                  :with-share="true"
                />
                <div class="notebox">
                  Facoltativo. Se lo lasci vuoto, le corse con un veicolo
                  <b>non immatricolato</b> si tariffano con il listino Fleet:
                  nessun errore, ma nessun prezzo diverso.
                </div>
              </div>

              <div
                v-if="form.services.transfer"
                class="subcard"
              >
                <div class="subcard__sh">
                  <span class="subcard__lbl">Tariffe Transfer</span>
                </div>
                <BandTable
                  v-model="form.bands.escort"
                  :default-mode="defaultModeFor('escort')"
                  :with-share="true"
                />
              </div>

              <div
                v-if="!anyService"
                class="apc__empty"
              >
                Scegli almeno un servizio al passaggio 1 per impostare le tariffe.
              </div>
            </template>
          </div>
        </section>

        <!-- ========================== 4 · DIPENDENTI ====================== -->
        <section
          id="s4"
          ref="s4"
          class="card"
        >
          <div class="card__hd">
            <span class="card__n">4</span>
            <h3>Dipendenti</h3>
            <span class="apc__sp" />
            <span class="hint">ruolo + assegnazione sedi</span>
          </div>
          <div class="card__bd">
            <div
              v-if="!form.employees.length"
              class="apc__empty"
            >
              Nessun dipendente: si possono aggiungere anche dopo, dalla scheda
              del partner.
            </div>

            <div
              v-for="(e, i) in form.employees"
              :key="i"
              class="subcard"
            >
              <div class="subcard__sh">
                <span class="subcard__lbl">Dipendente {{ i + 1 }}</span>
                <span class="apc__sp" />
                <el-button
                  type="danger"
                  plain
                  size="small"
                  @click="removeEmployee(i)"
                >
                  Rimuovi
                </el-button>
              </div>
              <div class="grid3">
                <div class="fld">
                  <label>Nome</label>
                  <el-input v-model="e.name" />
                </div>
                <div class="fld">
                  <label>Cognome</label>
                  <el-input v-model="e.surname" />
                </div>
                <div class="fld">
                  <label>Email</label>
                  <el-input v-model="e.mail" />
                </div>
                <div class="fld">
                  <label>Telefono</label>
                  <el-input v-model="e.telephone_number" />
                </div>
                <div class="fld">
                  <label>Ruolo</label>
                  <el-select
                    v-model="e.role"
                    style="width: 100%"
                  >
                    <el-option
                      v-for="r in ROLES"
                      :key="r.value"
                      :label="r.label"
                      :value="r.value"
                    />
                  </el-select>
                </div>
              </div>

              <div class="fld fld--spaced">
                <label>Sedi assegnate <span class="opt">(una o più)</span></label>
                <div class="chips">
                  <button
                    v-for="(h, hi) in namedHeadquarters"
                    :key="hi"
                    type="button"
                    class="schip"
                    :class="{ 'is-on': e.headquarterIdx.includes(hi) }"
                    @click="toggleSede(e, hi)"
                  >
                    {{ h.name || `Sede ${hi + 1}` }}
                  </button>
                  <button
                    type="button"
                    class="schip schip--all"
                    @click="allSedi(e)"
                  >
                    Tutte le sedi
                  </button>
                </div>
              </div>
            </div>

            <el-button
              class="apc__add"
              @click="addEmployee"
            >
              ＋ Aggiungi dipendente
            </el-button>
          </div>
        </section>
      </div>
    </div>

    <!-- ============================ BARRA AZIONI ========================= -->
    <div class="apc__footer">
      <div class="apc__footerIn">
        <span class="apc__prog">Compilazione partner · 4 passaggi</span>
        <span class="apc__sp" />
        <el-button @click="goBack">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="submit"
        >
          Crea partner
        </el-button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage, ElMessageBox } from 'element-plus';
import partnerAPI from '../../services/partnerAPI.js';
import prenotazioneAPI from '../../services/prenotazioneAPI.js';
import areasAPI from '../../services/areasAPI.js';
import BandTable from '../../components/ui/BandTable.vue';
import { defaultBandsFor, VALET_EXCESS_PER_KM } from '../../services/tariffDefaults.js';
import ValetBandTable from '../../components/ui/ValetBandTable.vue';
import {
  amountOr, bandPricing, bandRowsProblem, defaultModeFor, filledAmount, unitFor, unitForMode,
} from '../../services/tariffModel.js';

const STEPS = [
  { id: 's1', title: 'Dati partner', hint: 'tipo, anagrafica, Nexi' },
  { id: 's2', title: 'Sedi', hint: 'indirizzi, abbonamento' },
  { id: 's3', title: 'Tariffe & Valet', hint: 'in base al tipo' },
  { id: 's4', title: 'Dipendenti', hint: 'ruolo, sedi' },
];

const TYPES = [
  { value: 'automotive', icon: '🚗', label: 'Automotive', hint: 'Concessionarie e officine' },
  { value: 'notturno', icon: '🌙', label: 'Notturno', hint: 'Servizio serale / valet' },
];

const SERVICES = [
  { key: 'pickup', label: 'Pickup & Delivery' },
  { key: 'fleet', label: 'Fleet' },
  { key: 'transfer', label: 'Transfer' },
];

// I ruoli a UI mappano sull'enum di user.abstract.ts.
const ROLES = [
  { value: 'employee', label: 'Dipendente' },
  { value: 'salesvendor', label: 'Venditore' },
  { value: 'admin', label: 'Amministratore' },
];

const emptyHeadquarter = () => ({
  name: '', address: '', street_number: '', cap: '', city: '', province: '',
  // La provincia si sceglie da elenco (Regione → Provincia). La stringa
  // `province` resta a schema e viene tenuta allineata con la sigla.
  regionId: '', provinceId: '',
  abbonamento: null, latitude: null, longitude: null,
  geoState: '', geoLabel: '',
});

// Campi che compongono l'indirizzo da cercare: finché manca anche uno solo,
// geocodificare significa cercare un indirizzo diverso da quello vero.
const ADDRESS_FIELDS = ['address', 'street_number', 'cap', 'city', 'province'];
const addressComplete = (h) => ADDRESS_FIELDS.every((f) => String(h[f] || '').trim());

const emptyEmployee = () => ({
  name: '', surname: '', mail: '', telephone_number: '',
  role: 'employee', headquarterIdx: [],
});

// I listini predefiniti vivono in `services/tariffDefaults.js`: li usa anche la
// scheda partner, quando si aggiunge un listino a un partner che esiste già.
// Tenerli in due posti voleva dire vederli divergere al primo ritocco.

const valetBands = () => [
  { min_km: 0, max_km: 30, p1: 94.41, p2: 89.78, p3: 85.25, km: VALET_EXCESS_PER_KM },
  { min_km: 30, max_km: 50, p1: 136.37, p2: 129.69, p3: 123.14, km: VALET_EXCESS_PER_KM },
  { min_km: 50, max_km: 75, p1: 175.86, p2: 167.24, p3: 158.80, km: VALET_EXCESS_PER_KM },
  { min_km: 75, max_km: 100, p1: 218.15, p2: 207.46, p3: 196.99, km: VALET_EXCESS_PER_KM },
  { min_km: 100, max_km: 130, p1: 258.33, p2: 245.67, p3: 233.27, km: VALET_EXCESS_PER_KM },
  { min_km: 130, max_km: 160, p1: 287.91, p2: 280.77, p3: 271.90, km: VALET_EXCESS_PER_KM },
  { min_km: 160, max_km: 195, p1: 322.18, p2: 314.19, p3: 306.07, km: VALET_EXCESS_PER_KM },
];

export default {
  name: 'AddPartnerPage',
  components: { BandTable, ValetBandTable },

  setup() {
    const router = useRouter();
    const saving = ref(false);
    const advisors = ref([]);
    const activeStep = ref('s1');
    const s1 = ref(null);
    const s2 = ref(null);
    const s3 = ref(null);
    const s4 = ref(null);

    const form = reactive({
      type: 'automotive',
      services: { pickup: true, fleet: true, transfer: true },
      company_name: '',
      piva: '',
      mail: '',
      phone_number: '',
      provvigione: null,
      advisorId: '',
      API_key: '',
      domain: 'https://ecommerce.nexi.it',
      alias: '',
      has_extra_costs: false,
      headquarters: [emptyHeadquarter()],
      bands: {
        // Automotive: pickup e spostamenti partono dal listino concordato,
        // l'auto di cortesia si copia dal pickup con il suo pulsante.
        // L'auto di cortesia la riporta una persona sola: parte dalla
        // colonna a 1 operatore, il pickup da quella a 2.
        pickup: defaultBandsFor('pickup'),
        pickup_loaner: defaultBandsFor('pickup_loaner'),
        journey: defaultBandsFor('journey'),
        // Listino della targa prova: facoltativo, nasce vuoto (vedi template).
        journey_prova: [],
        escort: [],
        // Due listini DISTINTI: il backend sceglie `restaurant_night` per le
        // corse fra le 22 e le 6, `restaurant` per le altre. Si precompilano
        // uguali perché è il caso più comune, ma ora sono modificabili
        // separatamente invece di essere duplicati in silenzio al salvataggio.
        restaurant: defaultBandsFor('restaurant'),
        restaurant_night: defaultBandsFor('restaurant_night'),
      },
      valetBands: valetBands(),
      employees: [],
    });

    const isAutomotive = computed(() => form.type === 'automotive');
    const anyService = computed(() => Object.values(form.services).some(Boolean));
    const namedHeadquarters = computed(() => form.headquarters);

    // ------------------------------------------------------------- sedi ---
    const addHeadquarter = () => form.headquarters.push(emptyHeadquarter());
    const removeHeadquarter = (i) => {
      form.headquarters.splice(i, 1);
      // gli indici delle sedi assegnate ai dipendenti scalano di conseguenza
      for (const e of form.employees) {
        e.headquarterIdx = e.headquarterIdx
          .filter((idx) => idx !== i)
          .map((idx) => (idx > i ? idx - 1 : idx));
      }
    };

    // ------------------------------------------------ regioni e province ---
    const regions = ref([]);

    const loadGeo = async () => {
      try {
        regions.value = await areasAPI.getRegionsTree();
      } catch (e) {
        console.warn('Elenco regioni non disponibile:', e?.message);
      }
    };
    loadGeo();

    const provincesOf = (h) =>
      regions.value.find((r) => r.id === h.regionId)?.provinces || [];

    const onRegionChange = (h) => {
      h.provinceId = '';
      h.province = '';
      h.geoState = '';
    };

    // La colonna storica `province` è la SIGLA: si tiene allineata alla
    // provincia scelta, così i due dati non divergono.
    const onProvinceChange = (h) => {
      const p = provincesOf(h).find((x) => x.id === h.provinceId);
      h.province = p?.sigla || '';
      maybeGeocode(h);
    };

    // Sul blur dei campi: cerca solo quando l'indirizzo è completo, altrimenti
    // si limita a dire cosa manca.
    function maybeGeocode(h) {
      if (!addressComplete(h)) {
        if (h.geoState !== 'loading') h.geoState = 'incomplete';
        return;
      }
      geocode(h);
    }

    // L'endpoint restituisce un ARRAY di risultati con le coordinate dentro
    // `point`. Stesso formato usato dalle altre pagine.
    const geocode = async (h, forced = false) => {
      if (!addressComplete(h)) {
        h.geoState = 'incomplete';
        return;
      }
      if (h.geoState === 'ok' && !forced) return;
      h.geoState = 'loading';
      try {
        const text = `${h.address} ${h.street_number} ${h.cap}, ${h.city} ${h.province}`;
        const { data } = await prenotazioneAPI.getPoints({ text });
        const first = Array.isArray(data) ? data[0] : null;
        if (first?.point?.latitude) {
          h.latitude = first.point.latitude;
          h.longitude = first.point.longitude;
          h.geoLabel = first.label || text;
          h.geoState = 'ok';
        } else {
          h.geoState = 'fail';
        }
      } catch (e) {
        console.warn('Geocoding non riuscito:', e?.message);
        h.geoState = 'fail';
      }
    };

    // ------------------------------------------------------- dipendenti ---
    const addEmployee = () => form.employees.push(emptyEmployee());
    const removeEmployee = (i) => form.employees.splice(i, 1);
    const toggleSede = (e, hi) => {
      const at = e.headquarterIdx.indexOf(hi);
      if (at >= 0) e.headquarterIdx.splice(at, 1);
      else e.headquarterIdx.push(hi);
    };
    const allSedi = (e) => {
      e.headquarterIdx = form.headquarters.map((_, i) => i);
    };

    // ---------------------------------------------------------- stepper ---
    const goToStep = (id) => {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const onScroll = () => {
      const y = window.scrollY + 140;
      let current = STEPS[0].id;
      for (const s of STEPS) {
        const el = document.getElementById(s.id);
        if (el && el.offsetTop <= y) current = s.id;
      }
      activeStep.value = current;
    };

    // ------------------------------------------------------- validazione ---
    const validate = () => {
      if (!form.company_name.trim()) return 'Manca la ragione sociale';
      if (!form.mail.trim()) return 'Manca la mail del partner';
      if (isAutomotive.value && !anyService.value) {
        return 'Scegli almeno un servizio attivo';
      }
      // Le corse serali sono la norma per un notturno: lasciarle vuote
      // significa quotarle 0, ed è un errore che si scopre tardi.
      if (!isAutomotive.value && !form.bands.restaurant_night.length) {
        return 'Il notturno non ha fasce serali: le corse fra le 22 e le 6 quoterebbero 0';
      }
      // Le fasce che verranno salvate: stesse regole del backend, dette qui
      // invece di scoprirle da un salvataggio riuscito a metà.
      const listini = isAutomotive.value
        ? [
          ...(form.services.pickup
            ? [['pickup', form.bands.pickup], ['pickup_loaner', form.bands.pickup_loaner]]
            : []),
          ...(form.services.transfer ? [['escort', form.bands.escort]] : []),
          ...(form.services.fleet ? [['journey', form.bands.journey]] : []),
          ...(form.services.fleet && form.bands.journey_prova.length
            ? [['journey', form.bands.journey_prova]]
            : []),
        ]
        : [['restaurant', form.bands.restaurant], ['restaurant_night', form.bands.restaurant_night]];
      for (const [service, rows] of listini) {
        const problem = bandRowsProblem(rows, service);
        if (problem) return problem;
      }
      // Listino valet: il €/km dei km oltre il pacchetto è facoltativo, ma il
      // backend non rifiuta un negativo (lo salverebbe e lo addebiterebbe).
      if (isAutomotive.value) {
        // niente fasce senza limite: ognuna ha il suo km finale
        const unbounded = form.valetBands.find((b) => b.max_km === null || b.max_km === ''
          || b.max_km === undefined || Number(b.max_km) <= Number(b.min_km));
        if (unbounded) {
          return `Listino valet, fascia da ${unbounded.min_km} km: serve un km finale oltre l'inizio`;
        }
        const negative = form.valetBands.find((b) => b.km !== null && b.km !== undefined
          && b.km !== '' && Number(b.km) < 0);
        if (negative) {
          return `Listino valet, fascia da ${negative.min_km} km: il prezzo al km non può essere negativo`;
        }
      }
      const firstHq = form.headquarters[0];
      if (!firstHq?.name?.trim() || !firstHq?.address?.trim()) {
        return 'La prima sede deve avere almeno nome e indirizzo';
      }
      for (const e of form.employees) {
        if (!e.name.trim() || !e.surname.trim() || !e.mail.trim()) {
          return 'Ogni dipendente deve avere nome, cognome e mail';
        }
      }
      return null;
    };

    // Le fasce del form diventano righe `tariff_band`, valide da oggi.
    //
    // Il MODO di calcolo dipende dal servizio e non è uniforme: pickup,
    // transfer e notturno hanno un prezzo pieno per corsa, il fleet una
    // tariffa al km. La regola sta in tariffModel.js, unica per tutte le
    // pagine — sbagliarla non dà errore, dà un prezzo plausibile e sbagliato.
    // `immatricolato`: `false` = fascia dei veicoli NON immatricolati (targa
    // prova); `null` = fascia che vale in ogni caso.
    const bandPayload = (partnerId, service, b, operators = null, share = false, immatricolato = null) => ({
      partner: { id: partnerId },
      service,
      min_km: b.min_km,
      max_km: b.max_km,
      operators,
      is_partner_share: share,
      immatricolato,
      // la quota partner vive su righe proprie, con lo stesso disegno di fasce
      // il modo è quello della riga: un listino può mescolarli
      // il €/km è di ogni colonna: quello della quota, se vuoto, vale il primo
      ...bandPricing(
        service,
        share ? b.price_share : b.price,
        share ? amountOr(b.price_share_per_km, b.price_per_km) : b.price_per_km,
        b.mode || defaultModeFor(service),
      ),
      valid_from: new Date().toISOString().slice(0, 10),
    });

    // Una riga di fascia -> le righe da salvare: tariffa cliente e, se
    // compilata, quota partner.
    const bandJobsFor = (partnerId, service, b, operators = null) => {
      const out = [bandPayload(partnerId, service, b, operators)];
      if (b.price_share != null && b.price_share !== '') {
        out.push(bandPayload(partnerId, service, b, operators, true));
      }
      return out;
    };

    // Una riga di fascia Fleet -> le sue righe: il fleet distingue 1 e 2
    // operatori, quindi due righe per la tariffa e, se compilate, due per la
    // quota partner. Vale per il listino normale e per quello della targa
    // prova, che differiscono solo per `immatricolato`.
    const journeyJobsFor = (partnerId, b, immatricolato = null) => {
      const out = [
        bandPayload(partnerId, 'journey', { ...b, price: b.price }, 1, false, immatricolato),
        bandPayload(partnerId, 'journey', {
          ...b,
          price: amountOr(b.price2, b.price),
          price_per_km: amountOr(b.price2_per_km, b.price_per_km),
        }, 2, false, immatricolato),
      ];
      // Quota partner, se compilata: righe proprie, una per numero di
      // operatori. Senza quella a 2, il preventivo del partner per una corsa
      // a 2 operatori non trova la fascia.
      if (filledAmount(b.price_share)) {
        out.push(
          bandPayload(partnerId, 'journey', {
            ...b,
            price_share: b.price_share,
            price_share_per_km: amountOr(b.price_share_per_km, b.price_per_km),
          }, 1, true, immatricolato)
        );
      }
      if (filledAmount(b.price_share2)) {
        out.push(
          bandPayload(partnerId, 'journey', {
            ...b,
            price_share: b.price_share2,
            price_share_per_km: amountOr(
              b.price_share2_per_km,
              amountOr(b.price_share_per_km, b.price_per_km),
            ),
          }, 2, true, immatricolato)
        );
      }
      return out;
    };

    // La targa prova parte quasi sempre dal listino Fleet: si copia e si
    // ritoccano gli importi.
    const copyJourneyToProva = () => {
      form.bands.journey_prova = form.bands.journey.map((b) => ({ ...b }));
    };

    // L'auto di cortesia parte quasi sempre dal listino pickup: si copia e si
    // ritocca, invece di riscriverlo da zero.
    const copyPickupBands = () => {
      form.bands.pickup_loaner = form.bands.pickup.map((b) => ({ ...b }));
    };

    // Stesso ragionamento fra le due fasce del notturno: quasi sempre serale
    // e diurno coincidono, ma ora è una scelta esplicita e non una copia
    // automatica fatta al salvataggio.
    const copyDayToNight = () => {
      form.bands.restaurant_night = form.bands.restaurant.map((b) => ({ ...b }));
      ElMessage.success('Fasce serali allineate a quelle diurne.');
    };

    // ---------------------------------------------------------- salvataggio ---
    const submit = async () => {
      const problem = validate();
      if (problem) {
        ElMessage.warning(problem);
        return;
      }

      saving.value = true;
      const done = [];
      const failed = [];
      let partnerId = null;

      try {
        // 1 — il partner. Se fallisce questo non si va oltre.
        const res = await partnerAPI.createPartner({
          company_name: form.company_name,
          piva: form.piva,
          mail: form.mail,
          phone_number: form.phone_number,
          provvigione: form.provvigione ?? 0,
          API_key: form.API_key,
          domain: form.domain,
          alias: form.alias,
          has_extra_costs: form.has_extra_costs,
          is_restaurant: !isAutomotive.value,
          // i flag di capability guidano la fatturazione (COD3)
          booking_type: !isAutomotive.value || !!form.services.pickup,
          journey_type: isAutomotive.value && !!form.services.fleet,
        });
        partnerId = res?.data?.id;
        if (!partnerId) throw new Error('Il backend non ha restituito l\'id del partner');
        done.push('partner');
      } catch (e) {
        saving.value = false;
        console.error('Creazione partner fallita:', e);
        ElMessage.error(
          e?.response?.status === 403
            ? 'I partner possono essere creati solo da Simba'
            : 'Creazione del partner non riuscita: nulla è stato salvato'
        );
        return;
      }

      // 2 — sedi. Si tiene l'id creato: serve ai dipendenti.
      const createdHqIds = [];
      for (const h of form.headquarters) {
        if (!h.name?.trim() && !h.address?.trim()) continue;
        try {
          const r = await partnerAPI.addHeadquarter({
            name: h.name,
            address: h.address,
            street_number: h.street_number,
            cap: h.cap,
            city: h.city,
            // sigla, allineata alla provincia scelta dall'elenco
            province: h.province,
            abbonamento: h.abbonamento ?? 0,
            latitude: h.latitude ?? 0,
            longitude: h.longitude ?? 0,
            partner: { id: partnerId },
            // Le relazioni vere. `province_ref` si chiama così perché la
            // colonna stringa `province` occupava già il nome.
            ...(h.regionId ? { region: { id: h.regionId } } : {}),
            ...(h.provinceId ? { province_ref: { id: h.provinceId } } : {}),
          });
          createdHqIds.push(r?.data?.id ?? null);
        } catch (e) {
          console.error('Creazione sede fallita:', e);
          createdHqIds.push(null);
          failed.push(`sede "${h.name || h.address}"`);
        }
      }
      if (createdHqIds.some(Boolean)) done.push('sedi');

      // 3 — fasce tariffarie e valet
      const bandJobs = [];
      if (isAutomotive.value) {
        if (form.services.pickup) {
          for (const b of form.bands.pickup) bandJobs.push(...bandJobsFor(partnerId, 'pickup', b));
          for (const b of form.bands.pickup_loaner) {
            bandJobs.push(...bandJobsFor(partnerId, 'pickup_loaner', b));
          }
        }
        if (form.services.transfer) {
          for (const b of form.bands.escort) bandJobs.push(...bandJobsFor(partnerId, 'escort', b));
        }
        if (form.services.fleet) {
          for (const b of form.bands.journey) bandJobs.push(...journeyJobsFor(partnerId, b));
          // il listino della targa prova, se compilato
          for (const b of form.bands.journey_prova) {
            bandJobs.push(...journeyJobsFor(partnerId, b, false));
          }
        }
      } else {
        // Due listini distinti, ciascuno dal proprio array: il backend usa
        // `restaurant_night` per le corse fra le 22 e le 6 e `restaurant`
        // per le altre.
        for (const b of form.bands.restaurant) {
          bandJobs.push(...bandJobsFor(partnerId, 'restaurant', b));
        }
        for (const b of form.bands.restaurant_night) {
          bandJobs.push(...bandJobsFor(partnerId, 'restaurant_night', b));
        }
      }
      // Tutte in UNA richiesta: con i listini precompilati sono una novantina
      // di fasce, e una POST per ciascuna (più il preflight CORS) lasciava il
      // salvataggio a girare. La `bulk` le scrive tutte insieme o nessuna.
      if (bandJobs.length) {
        try {
          await partnerAPI.createBands(bandJobs);
          done.push('tariffe');
        } catch (e) {
          console.error('Creazione delle fasce fallita:', e);
          failed.push('fasce tariffarie');
        }
      }

      // Un prezzo non scritto resta vuoto: quella variante non si vende.
      const valetPrice = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
      if (isAutomotive.value && form.valetBands.length) {
        const r = await Promise.allSettled(
          form.valetBands.map((b) =>
            partnerAPI.createValetBand({
              partner: { id: partnerId },
              from_km: b.min_km,
              to_km: b.max_km,
              annual_price_1: valetPrice(b.p1),
              annual_price_2: valetPrice(b.p2),
              annual_price_3: valetPrice(b.p3),
              excess_price_per_km: valetPrice(b.km),
            })
          )
        );
        const ko = r.filter((x) => x.status === 'rejected').length;
        if (ko) failed.push(`${ko} fasce valet`);
        if (ko < r.length) done.push('fasce valet');
      }

      // 4 — advisor: la M2M è dichiarata sull'Advisor, si scrive di là
      if (form.advisorId) {
        try {
          const adv = advisors.value.find((a) => a.id === form.advisorId);
          const existing = (adv?.partners || []).map((p) => p.id);
          await partnerAPI.linkAdvisorToPartner(form.advisorId, [...existing, partnerId]);
          done.push('advisor');
        } catch (e) {
          console.error('Collegamento advisor fallito:', e);
          failed.push('advisor');
        }
      }

      // 5 — dipendenti
      //
      // ⚠️ Il dipendente va legato al PARTNER, non solo alle sedi.
      // `employee.partnerId` è la regola di dominio dichiarata sull'entità
      // ("1 account = 1 partner") ed è ciò che lo rende visibile e scopato
      // per tenant. Senza, e senza sedi spuntate, il dipendente veniva creato
      // davvero ma restava ORFANO: il dettaglio partner elenca i dipendenti
      // passando da `headquarters.partner.id`, quindi non compariva da
      // nessuna parte e sembrava non creato.
      //
      // Se non è stata spuntata nessuna sede si collegano TUTTE quelle del
      // partner: un dipendente senza sede non è rappresentabile nel modello,
      // e lasciarlo scollegato è il caso che ha prodotto la segnalazione.
      for (const e of form.employees) {
        const scelte = e.headquarterIdx.map((i) => createdHqIds[i]).filter(Boolean);
        const hqIds = scelte.length ? scelte : createdHqIds.filter(Boolean);
        try {
          await partnerAPI.addEmployee({
            name: e.name,
            surname: e.surname,
            mail: e.mail,
            telephone_number: e.telephone_number,
            role: e.role,
            partner: { id: partnerId },
            headquarters: hqIds.map((id) => ({ id })),
          });
        } catch (err) {
          console.error('Creazione dipendente fallita:', err);
          failed.push(`dipendente ${e.name} ${e.surname}`);
        }
      }
      if (form.employees.length && !failed.length) done.push('dipendenti');

      saving.value = false;

      // La creazione non è atomica: si dice esattamente cosa è stato salvato
      // e cosa no, altrimenti resterebbe da indovinare.
      if (failed.length) {
        await ElMessageBox.alert(
          `Il partner è stato creato, ma alcune parti non sono state salvate: ${failed.join(', ')}. ` +
            'Completale dalla sua scheda.',
          'Creazione parziale',
          { confirmButtonText: 'Apri la scheda' }
        );
      } else {
        ElMessage.success('Partner creato');
      }
      router.push(`/partner/${partnerId}`);
    };

    const goBack = () => router.push('/partner');

    onMounted(async () => {
      window.addEventListener('scroll', onScroll, { passive: true });
      try {
        advisors.value = await partnerAPI.getAdvisors();
      } catch (e) {
        console.warn('Elenco advisor non disponibile:', e?.message);
      }
    });
    onBeforeUnmount(() => window.removeEventListener('scroll', onScroll));

    return {
      form, advisors, saving, activeStep, s1, s2, s3, s4,
      STEPS, TYPES, SERVICES, ROLES,
      isAutomotive, anyService, namedHeadquarters,
      addHeadquarter, removeHeadquarter, geocode, maybeGeocode, copyPickupBands, copyDayToNight,
      copyJourneyToProva,
      unitFor, unitForMode, defaultModeFor,
      regions, provincesOf, onRegionChange, onProvinceChange,
      addEmployee, removeEmployee, toggleSede, allSedi,
      goToStep, submit, goBack,
    };
  },
};
</script>

<style scoped>
.apc { padding: 4px 0 90px; }
.apc__sp { flex: 1; }

.apc__head {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap; margin: 0 2px 18px;
}
.apc__back {
  width: 36px; height: 36px; flex: none; border-radius: 9px;
  border: 1px solid var(--border-color); background: var(--bg-primary);
  display: grid; place-items: center; cursor: pointer;
  font-size: 16px; color: var(--text-secondary); font-family: inherit;
}
.apc__back:hover { border-color: var(--brand-violet); }
.apc__title { margin: 0; font-size: 21px; color: var(--text-primary); }
.apc__sub { margin: 2px 0 0; font-size: 13px; color: var(--text-tertiary); }

.apc__layout {
  display: grid; grid-template-columns: 230px 1fr; gap: 20px; align-items: start;
}
@media (max-width: 840px) { .apc__layout { grid-template-columns: 1fr; } }
.apc__col { display: flex; flex-direction: column; gap: 16px; }

.stepper {
  position: sticky; top: 16px; display: flex; flex-direction: column; gap: 2px;
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); padding: 10px;
}
@media (max-width: 840px) {
  .stepper { position: static; flex-direction: row; overflow-x: auto; }
}
.step {
  display: flex; align-items: center; gap: 11px; padding: 10px 11px;
  border-radius: 9px; cursor: pointer; color: var(--text-secondary);
  border: 1px solid transparent; background: transparent;
  font-family: inherit; text-align: left; white-space: nowrap;
}
.step:hover { background: var(--bg-tertiary); }
.step.is-active {
  background: var(--svc-fleet-bg); border-color: var(--brand-violet);
}
.step__n {
  width: 24px; height: 24px; border-radius: 50%; flex: none;
  display: grid; place-items: center; font-size: 12px; font-weight: 700;
  background: var(--bg-tertiary); color: var(--text-tertiary);
  border: 1px solid var(--border-color);
}
.step.is-active .step__n {
  background: var(--brand-violet); color: #fff; border-color: var(--brand-violet);
}
.step__t { font-weight: 600; font-size: 13px; }
.step__t small {
  display: block; color: var(--text-tertiary); font-weight: 500; font-size: 11px;
}

.card {
  background: var(--bg-primary); border: 1px solid var(--border-color);
  border-radius: var(--radius-lg); scroll-margin-top: 16px;
}
.card__hd {
  display: flex; align-items: center; gap: 11px;
  padding: 14px 16px; border-bottom: 1px solid var(--border-color);
}
.card__n {
  width: 24px; height: 24px; border-radius: 50%; flex: none;
  display: grid; place-items: center; font-size: 12px; font-weight: 700;
  background: var(--brand-violet); color: #fff;
}
.card__hd h3 { margin: 0; font-size: 15px; color: var(--text-primary); }
.card__bd { padding: 16px; }

.blocklabel {
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.05em;
  color: var(--text-tertiary); font-weight: 700; margin: 2px 0 10px;
}
.blocklabel--spaced { margin-top: 18px; }

.grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 13px; }
.grid3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 13px; }
@media (max-width: 620px) {
  .grid2, .grid3 { grid-template-columns: 1fr; }
}
.fld { display: flex; flex-direction: column; gap: 5px; }
.fld--spaced { margin-top: 12px; }
.fld--full { grid-column: 1 / -1; }
.geoRow { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
.fld label { font-size: 11.5px; font-weight: 600; color: var(--text-secondary); }
.fld label .opt { color: var(--text-tertiary); font-weight: 500; }

.hint { font-size: 11.5px; color: var(--text-tertiary); }
.hint--block { display: block; margin-top: 10px; }
.hint--attn { color: var(--state-attn); }

.geo {
  display: flex; align-items: center; gap: 6px; font-size: 12px;
  color: var(--state-ok); margin-top: 2px;
}
.geo--fail { color: var(--state-attn); }
.geo--wait { color: var(--text-tertiary); }
.geo b { color: var(--text-secondary); font-weight: 600; }

/* --------------------------------- tipo --------------------------------- */
.typecards { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
@media (max-width: 620px) { .typecards { grid-template-columns: 1fr; } }
.tcard { position: relative; cursor: pointer; display: block; }
.tcard input { position: absolute; opacity: 0; inset: 0; cursor: pointer; }
.tcard__in {
  display: flex; align-items: center; gap: 12px;
  border: 1.5px solid var(--border-color); border-radius: 11px; padding: 14px;
}
.tcard.is-on .tcard__in {
  border-color: var(--brand-violet); background: var(--svc-fleet-bg);
}
.tcard__ico {
  width: 40px; height: 40px; border-radius: 10px; flex: none;
  display: grid; place-items: center; font-size: 20px; background: var(--bg-tertiary);
}
.tcard__t { display: block; font-weight: 700; color: var(--text-primary); }
.tcard__h { display: block; font-size: 12px; color: var(--text-tertiary); }

/* ------------------------------- servizi -------------------------------- */
.svcsel { display: flex; flex-wrap: wrap; gap: 9px; }
.svcbox { position: relative; cursor: pointer; }
.svcbox input { position: absolute; opacity: 0; inset: 0; cursor: pointer; }
.svcbox__b {
  display: flex; align-items: center; gap: 8px;
  border: 1px solid var(--border-color); border-radius: 9px;
  padding: 9px 13px; font-weight: 600; font-size: 13px; color: var(--text-secondary);
}
.svcbox__b::before { content: "＋"; font-weight: 700; color: var(--text-tertiary); }
.svcbox.is-on .svcbox__b {
  border-color: var(--brand-violet); background: var(--svc-fleet-bg);
  color: var(--brand-violet);
}
.svcbox.is-on .svcbox__b::before { content: "✓"; color: var(--brand-violet); }

/* -------------------------------- toggle -------------------------------- */
.toggle-row {
  display: flex; align-items: center; gap: 12px; margin-top: 16px;
  background: var(--bg-secondary); border: 1px solid var(--border-color);
  border-radius: 10px; padding: 12px 14px;
}
.toggle-row__txt { flex: 1; }
.toggle-row__t { font-weight: 600; color: var(--text-primary); }
.toggle-row__h { font-size: 12px; color: var(--text-tertiary); }

/* ------------------------------- subcard -------------------------------- */
.subcard {
  border: 1px solid var(--border-color); border-radius: 10px;
  padding: 14px; background: var(--bg-secondary);
}
.subcard + .subcard { margin-top: 12px; }
.subcard__sh {
  display: flex; align-items: center; gap: 8px; margin-bottom: 11px; flex-wrap: wrap;
}
.subcard__lbl { font-weight: 700; font-size: 13px; color: var(--text-primary); }

.calcbox {
  background: var(--bg-primary); border: 1px dashed var(--border-color);
  border-radius: 9px; padding: 10px 13px; margin-top: 12px;
  display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
}
.calcbox__lab { color: var(--text-tertiary); font-size: 12px; }
.calcbox__v {
  font-weight: 750; font-variant-numeric: tabular-nums; color: var(--text-primary);
}

.notebox {
  font-size: 12px; color: var(--text-secondary); background: var(--svc-notturno-bg);
  border: 1px solid var(--border-color); border-radius: 9px;
  padding: 9px 12px; margin-top: 10px;
}

/* --------------------------------- chip --------------------------------- */
.chips { display: flex; flex-wrap: wrap; gap: 7px; align-items: center; }
.schip {
  border: 1px solid var(--border-color); background: var(--bg-primary);
  border-radius: 20px; padding: 5px 12px; font-size: 12.5px; font-weight: 600;
  cursor: pointer; color: var(--text-secondary); font-family: inherit;
}
.schip:hover { border-color: var(--brand-violet); }
.schip.is-on {
  background: var(--brand-violet); border-color: var(--brand-violet); color: #fff;
}
.schip--all {
  border-style: dashed; background: var(--bg-secondary); color: var(--text-secondary);
}

.apc__add { margin-top: 12px; }
.apc__empty { font-size: 13px; color: var(--text-tertiary); padding: 4px 0; }

/* ----------------------------- barra azioni ----------------------------- */
.apc__footer {
  position: fixed; left: 0; right: 0; bottom: 0; z-index: 20;
  background: var(--bg-primary); border-top: 1px solid var(--border-color);
  box-shadow: 0 -4px 16px rgba(24, 32, 64, 0.06); padding: 12px 22px;
}
.apc__footerIn {
  max-width: 1180px; margin: 0 auto; width: 100%;
  display: flex; align-items: center; gap: 12px;
}
.apc__prog { color: var(--text-tertiary); font-size: 12.5px; }
</style>
